import { useState, useEffect } from "react";

const Luma = () => {
  const [mode, setMode] = useState("menu"); // menu, checkin, reflect
  const [step, setStep] = useState(0);
  const [fadeState, setFadeState] = useState("visible");
  const [breathStep, setBreathStep] = useState(0);
  const [breathCycle, setBreathCycle] = useState(0);
  const [isBreathing, setIsBreathing] = useState(false);
  const [answers, setAnswers] = useState({});
  const [reflectionText, setReflectionText] = useState("");
  const [showDelayedButton, setShowDelayedButton] = useState(false);
  
  // Smooth transition to next step
  const transitionToNext = (delay = 0) => {
    setTimeout(() => {
      setFadeState("fading");
      setTimeout(() => {
        setStep(s => s + 1);
        setFadeState("visible");
      }, 400);
    }, delay);
  };

  const nextStep = () => {
    setFadeState("fading");
    setTimeout(() => {
      setStep(s => s + 1);
      setFadeState("visible");
    }, 400);
  };

  const selectAndNext = (key, value) => {
    setAnswers({...answers, [key]: value});
    nextStep();
  };

  // Auto-transition for grounding steps
  useEffect(() => {
    if (mode !== "checkin") return;
    
    // Step 4: "We're going to move through this together" - auto after 4 seconds
    if (step === 4) {
      const timer = setTimeout(() => transitionToNext(), 4000);
      return () => clearTimeout(timer);
    }
    
    // Step 5: "Find a comfortable position" - auto after 3 seconds
    if (step === 5) {
      const timer = setTimeout(() => transitionToNext(), 3000);
      return () => clearTimeout(timer);
    }
    
    // Step 6: Pre-instruction - user clicks "I'm ready" (not auto)
    
    // Step 7: Rub palms - auto after 5 seconds
    if (step === 7) {
      const timer = setTimeout(() => transitionToNext(), 5000);
      return () => clearTimeout(timer);
    }
    
    // Step 8: Hands on eyes - auto after 30 seconds
    if (step === 8) {
      const timer = setTimeout(() => transitionToNext(), 30000);
      return () => clearTimeout(timer);
    }
    
    // Step 9: Lower hands - auto after 3 seconds
    if (step === 9) {
      const timer = setTimeout(() => transitionToNext(), 3000);
      return () => clearTimeout(timer);
    }
    
    // Step 10: "Take five slow breaths" intro - auto after 4 seconds
    if (step === 10) {
      const timer = setTimeout(() => transitionToNext(), 4000);
      return () => clearTimeout(timer);
    }
    
    // Step 11: Breathing "Ready?" screen - auto-start breathing after 2 seconds
    if (step === 11 && !isBreathing && breathCycle === 0 && breathStep === 0) {
      const timer = setTimeout(() => setIsBreathing(true), 2000);
      return () => clearTimeout(timer);
    }
    
    // Step 13: "That's all that matters" - auto after 3 seconds
    if (step === 13) {
      const timer = setTimeout(() => transitionToNext(), 3000);
      return () => clearTimeout(timer);
    }
  }, [step, mode, isBreathing, breathCycle, breathStep]);

  // Breathing exercise - 5 cycles
  useEffect(() => {
    if (!isBreathing) return;
    
    const durations = {
      0: 1000, 1: 1000, 2: 1000, 3: 1000, // inhale (4 counts)
      4: 1000, 5: 1000, 6: 1000, 7: 1000, 8: 1000, 9: 1000, // exhale (6 counts)
    };

    const timer = setTimeout(() => {
      if (breathStep < 9) {
        setBreathStep(breathStep + 1);
      } else {
        if (breathCycle < 4) { // 5 cycles (0-4)
          setBreathCycle(breathCycle + 1);
          setBreathStep(0);
        } else {
          setIsBreathing(false);
          transitionToNext();
        }
      }
    }, durations[breathStep] || 1000);

    return () => clearTimeout(timer);
  }, [isBreathing, breathStep, breathCycle]);

  const getBreathText = () => {
    const inhaleTexts = ["In…", "2…", "3…", "4…"];
    const exhaleTexts = ["Out…", "2…", "3…", "4…", "5…", "6…"];
    
    if (breathStep < 4) {
      return { text: inhaleTexts[breathStep], phase: "inhale", label: "In through the nose…" };
    } else {
      return { text: exhaleTexts[breathStep - 4], phase: "exhale", label: "Out through the mouth…" };
    }
  };

  // Main check-in flow
  const checkinSteps = [
    // Step 0: Welcome
    {
      content: (
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Welcome to Luma.
          </h1>
          <div className="space-y-6 text-[#6B6B6B] text-lg leading-relaxed max-w-md mx-auto">
            <p>This is a short guided check-in.</p>
            <p>You'll follow a few simple steps, take a gentle breath, and finish with one grounded next step.</p>
            <p className="text-[#3D3D3D] font-medium">This is not a quiz.</p>
            <p>No experience needed.<br/>Just follow one step at a time.</p>
          </div>
        </div>
      ),
      button: "Begin"
    },

    // Step 1: STATE (choice)
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Check-in</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What feels most true right now?
          </h2>
          <div className="space-y-3 max-w-sm mx-auto">
            {[
              "Overwhelmed",
              "Anxious / activated",
              "Flat / heavy",
              "Stuck / avoiding",
              "Scattered / unfocused",
              "I feel okay",
              "I'm not sure"
            ].map((option) => (
              <button
                key={option}
                onClick={() => {
                  if (option === "I feel okay") {
                    setAnswers({...answers, state: option});
                    setMode("okay");
                    setStep(0);
                  } else {
                    selectAndNext("state", option);
                  }
                }}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
                data-testid={`luma-state-${option.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },

    // Step 2: BODY LOCATION
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Check-in</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Where do you feel this most clearly?
          </h2>
          <p className="text-[#8B8B8B] italic mb-10">Just notice it as it is.</p>
          <div className="space-y-3 max-w-sm mx-auto">
            {[
              "Chest",
              "Belly",
              "Head",
              "Throat",
              "Whole body",
              "Not sure"
            ].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("bodyLocation", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },

    // Step 3: GENTLE ORIENTATION
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-4xl mb-6">✓</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Good.
          </h2>
          <div className="space-y-4 text-[#6B6B6B] text-lg">
            <p>Let's bring your attention there for a moment.</p>
            <p className="text-[#8B8B8B] italic">You don't need to change anything.<br/>Just notice.</p>
          </div>
        </div>
      ),
      button: "Continue"
    },

    // Step 4: Preparation (AUTO - 4 seconds)
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            We're going to move through this together.
          </h2>
          <div className="space-y-4 text-[#6B6B6B] text-lg">
            <p>Just follow each step as it appears.</p>
            <p className="text-[#8B8B8B] italic">You don't need to remember anything.</p>
          </div>
        </div>
      ),
      auto: true
    },

    // Step 5: Transition to grounding (AUTO - 3 seconds)
    {
      content: (
        <div className="text-center">
          <p className="text-[#8B8B8B] text-sm uppercase tracking-widest mb-6">Let's begin</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Find a comfortable position.
          </h2>
        </div>
      ),
      auto: true
    },

    // Step 6: Pre-instruction for eyes-closed moment
    {
      content: (
        <div className="text-center">
          <div className="space-y-6 text-[#3D3D3D] text-lg md:text-xl leading-relaxed">
            <p>In a moment, you can gently close your eyes.</p>
            <p className="text-[#6B6B6B]">You'll take 3 slow breaths.</p>
            <p className="text-[#6B6B6B]">Then open your eyes again when you're ready.</p>
          </div>
        </div>
      ),
      button: "I'm ready"
    },

    // Step 7: Rub palms (AUTO - 5 seconds)
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Rub your palms together for a few seconds.
          </h2>
          <p className="text-[#8B8B8B] text-lg italic">Notice the warmth building.</p>
        </div>
      ),
      auto: true
    },

    // Step 8: Hands on eyes (AUTO - 30 seconds)
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Place the warmest part of your hands gently over your eyes.
          </h2>
          <p className="text-[#8B8B8B] text-lg italic">Let them rest there for 5 slow, relaxed breaths.</p>
        </div>
      ),
      auto: true
    },

    // Step 9: Lower hands (AUTO - 3 seconds)
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Lower your hands.
          </h2>
          <p className="text-[#6B6B6B] text-lg">Keep your eyes open.</p>
        </div>
      ),
      auto: true
    },

    // Step 10: Five breaths intro (AUTO - 4 seconds)
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Take five slow breaths with me.
          </h2>
          <div className="space-y-2 text-[#6B6B6B] text-lg">
            <p>In through the nose… 2… 3… 4…</p>
            <p>Out through the mouth… 2… 3… 4… 5… 6…</p>
          </div>
          <p className="text-[#8B8B8B] italic mt-6">Just follow along.</p>
        </div>
      ),
      auto: true
    },

    // Step 11: Breathing exercise (5 cycles) - auto-starts
    {
      content: (
        <div className="text-center">
          {!isBreathing && breathCycle === 0 && breathStep === 0 ? (
            <>
              <p className="text-[#8B8B8B] text-lg">Starting...</p>
            </>
          ) : isBreathing ? (
            <>
              <p className="text-[#8B8B8B] text-sm uppercase tracking-widest mb-4">
                Breath {breathCycle + 1} of 5
              </p>
              <p className="text-[#6B6B6B] text-lg mb-6">{getBreathText().label}</p>
              <h2 
                className="text-5xl md:text-6xl font-serif text-[#3D3D3D] my-8" 
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {getBreathText().text}
              </h2>
            </>
          ) : (
            <>
              <p className="text-[#C9A66B] text-4xl mb-6">✓</p>
              <h2 className="text-2xl font-serif text-[#3D3D3D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Well done.
              </h2>
              <p className="text-[#6B6B6B]">Take a moment.</p>
            </>
          )}
        </div>
      ),
      button: (!isBreathing && breathCycle > 0) ? "Continue" : null,
      skipButton: isBreathing || (breathCycle === 0 && breathStep === 0)
    },

    // Step 12: RETURN TO CONTEXT
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Notice</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What feels different, even slightly?
          </h2>
          <div className="space-y-3 max-w-sm mx-auto">
            {[
              "Less pressure",
              "A little more space",
              "About the same",
              "I feel something emotional",
              "Not sure"
            ].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("difference", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },

    // Step 13: Acknowledgment (AUTO - 3 seconds)
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            That's all that matters.
          </h2>
          <p className="text-[#6B6B6B] text-lg">Even small shifts count.</p>
        </div>
      ),
      auto: true
    },

    // Step 14: NEXT STEP intro
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Final step</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            One small step forward.
          </h2>
          <p className="text-[#6B6B6B] text-lg">Something to carry this with you.</p>
        </div>
      ),
      button: "Show me"
    },

    // Step 15: Choose next step
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Choose one</p>
          <h2 className="text-2xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What is one small step you can take from here?
          </h2>
          <div className="space-y-3 max-w-sm mx-auto">
            {[
              "Drink a glass of water",
              "Step outside for 2 minutes",
              "Write down one thing",
              "Do one small task",
              "Rest for 5 minutes",
              "Just sit with this"
            ].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("nextStep", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },

    // Step 16: Completion
    {
      content: (
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#C9A66B]/20 flex items-center justify-center mx-auto mb-8">
            <span className="text-[#C9A66B] text-2xl">✓</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            You've completed Luma.
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-2">Your next step:</p>
          <p className="text-[#3D3D3D] text-xl font-medium mb-8">{answers.nextStep || "Take one grounded action"}</p>
          <p className="text-[#8B8B8B] italic">You don't need to feel different.<br/>Just move well from here.</p>
        </div>
      ),
      button: "Finish"
    },

    // Step 17: End
    {
      content: (
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Thank you.
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-8">Return whenever you need a reset.</p>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => { setMode("menu"); setStep(0); setBreathCycle(0); setBreathStep(0); setAnswers({}); }}
                className="inline-block bg-white border border-[#E8E4DE] text-[#3D3D3D] px-8 py-4 rounded-full hover:border-[#C9A66B] transition-colors"
                data-testid="luma-checkin-try-again"
              >
                Try Again
              </button>
              <a 
                href="/live"
                className="inline-block bg-[#C9A66B] text-white px-8 py-4 rounded-full hover:bg-[#B8956A] transition-colors"
                data-testid="luma-checkin-join-live"
              >
                Join a Live Session
              </a>
            </div>
          </div>
        </div>
      ),
      skipButton: true
    }
  ];

  // Reflection flow (Talk to Luma)
  const reflectSteps = [
    {
      content: (
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Talk to Luma
          </h1>
          <p className="text-[#6B6B6B] text-lg mb-4">This is a quiet space to reflect.</p>
          <p className="text-[#8B8B8B] italic">No advice. No solutions. Just clarity.</p>
        </div>
      ),
      button: "Begin"
    },
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Reflection</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What's on your mind right now?
          </h2>
          <div className="max-w-sm mx-auto">
            <textarea
              value={reflectionText}
              onChange={(e) => setReflectionText(e.target.value)}
              placeholder="Write freely... or leave blank"
              className="w-full h-32 bg-white border border-[#E8E4DE] rounded-xl px-4 py-3 text-[#3D3D3D] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A66B]/50 resize-none"
            />
            <p className="text-[#8B8B8B] text-sm mt-2 italic">This is just for you.</p>
          </div>
        </div>
      ),
      button: "Continue"
    },
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Reflection</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Where do you feel that in your body?
          </h2>
          <p className="text-[#8B8B8B] italic mb-10">Just notice it as it is.</p>
          <div className="space-y-3 max-w-sm mx-auto">
            {["Chest", "Belly", "Head", "Throat", "Whole body", "Not sure"].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("bodyLocation", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },
    {
      content: (
        <div className="text-center">
          <div className="space-y-6 text-[#3D3D3D] text-xl md:text-2xl font-serif leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <p>Take a slow breath.</p>
            <p className="text-[#6B6B6B]">In through your nose… gently…</p>
            <p className="text-[#6B6B6B]">Out through your mouth… slowly…</p>
            <p>Again.</p>
            <p className="text-[#8B8B8B] text-lg mt-8">Let your attention rest where you felt it in your body.</p>
            <p className="text-[#8B8B8B] italic text-lg">You don't need to change anything.<br/>Just notice.</p>
          </div>
        </div>
      ),
      button: "Continue",
      buttonDelay: 3000
    },
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Reflection</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What would feel like a small step forward?
          </h2>
          <div className="space-y-3 max-w-sm mx-auto">
            {[
              "Say what I'm feeling out loud",
              "Write it down somewhere",
              "Let it go for now",
              "Take a walk",
              "Reach out to someone",
              "Just sit with it"
            ].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("smallStep", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },
    {
      content: (
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#C9A66B]/20 flex items-center justify-center mx-auto mb-8">
            <span className="text-[#C9A66B] text-2xl">✓</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            That's enough for now.
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-2">Your small step:</p>
          <p className="text-[#3D3D3D] text-xl font-medium mb-4">{answers.smallStep || "Take your time"}</p>
          <p className="text-[#8B8B8B] italic mb-6">You can take that gently, in your own time.</p>
          <p className="text-[#8B8B8B] italic text-sm">Come back to that when you're ready.</p>
        </div>
      ),
      button: "Finish"
    },
    {
      content: (
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Thank you for reflecting.
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-8">Return whenever you need this space.</p>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => { setMode("menu"); setStep(0); setAnswers({}); setReflectionText(""); }}
                className="inline-block bg-white border border-[#E8E4DE] text-[#3D3D3D] px-8 py-4 rounded-full hover:border-[#C9A66B] transition-colors"
                data-testid="luma-reflect-try-again"
              >
                Try Again
              </button>
              <a 
                href="/live"
                className="inline-block bg-[#C9A66B] text-white px-8 py-4 rounded-full hover:bg-[#B8956A] transition-colors"
                data-testid="luma-reflect-join-live"
              >
                Join a Live Session
              </a>
            </div>
          </div>
        </div>
      ),
      skipButton: true
    }
  ];

  // "I feel okay" pathway - for users who are not distressed but want alignment
  const okaySteps = [
    // Step 0: Acknowledgment
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-4xl mb-6">✓</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            That's a good place to start.
          </h2>
          <p className="text-[#6B6B6B] text-lg">Let's see if we can support that.</p>
        </div>
      ),
      button: "Continue"
    },

    // Step 1: What would you like more of?
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Check-in</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What would you like more of right now?
          </h2>
          <div className="space-y-3 max-w-sm mx-auto">
            {["Calm", "Clarity", "Energy", "Focus"].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("desire", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },

    // Step 2: Uplifting breath intro
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Let's take a moment to support that.
          </h2>
          <p className="text-[#6B6B6B] text-lg">A few slow, intentional breaths.</p>
        </div>
      ),
      button: "I'm ready"
    },

    // Step 3: Uplifting breathing moment
    {
      content: (
        <div className="text-center">
          <div className="space-y-6 text-[#3D3D3D] text-xl md:text-2xl font-serif leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <p>Breathe in slowly…</p>
            <p className="text-[#6B6B6B]">Feel your chest lift gently.</p>
            <p>Breathe out…</p>
            <p className="text-[#6B6B6B]">Let your shoulders soften.</p>
            <p className="mt-8">Once more.</p>
            <p className="text-[#8B8B8B] text-lg mt-8 italic">Notice what's already here.</p>
          </div>
        </div>
      ),
      button: "Continue",
      buttonDelay: 3000
    },

    // Step 4: Small action
    {
      content: (
        <div className="text-center">
          <p className="text-[#C9A66B] text-sm uppercase tracking-widest mb-6">Support</p>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What feels like one small way to support that?
          </h2>
          <div className="space-y-3 max-w-sm mx-auto">
            {[
              "Take a short walk",
              "Drink some water",
              "Stretch for a minute",
              "Write down a thought",
              "Step outside briefly",
              "Just carry this feeling"
            ].map((option) => (
              <button
                key={option}
                onClick={() => selectAndNext("support", option)}
                className="w-full p-4 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all"
              >
                <span className="text-[#3D3D3D]">{option}</span>
              </button>
            ))}
          </div>
        </div>
      ),
      skipButton: true
    },

    // Step 5: Soft closing
    {
      content: (
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#C9A66B]/20 flex items-center justify-center mx-auto mb-8">
            <span className="text-[#C9A66B] text-2xl">✓</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            You're already on your way.
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-2">Your intention:</p>
          <p className="text-[#3D3D3D] text-xl font-medium mb-4">{answers.support || "Carry this feeling"}</p>
          <p className="text-[#8B8B8B] italic">Small steps add up. Trust the direction.</p>
        </div>
      ),
      button: "Finish"
    },

    // Step 6: End
    {
      content: (
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Thank you.
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-8">Return whenever you'd like a moment of alignment.</p>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => { setMode("menu"); setStep(0); setAnswers({}); }}
                className="inline-block bg-white border border-[#E8E4DE] text-[#3D3D3D] px-8 py-4 rounded-full hover:border-[#C9A66B] transition-colors"
                data-testid="luma-okay-try-again"
              >
                Try Again
              </button>
              <a 
                href="/live"
                className="inline-block bg-[#C9A66B] text-white px-8 py-4 rounded-full hover:bg-[#B8956A] transition-colors"
                data-testid="luma-okay-join-live"
              >
                Join a Live Session
              </a>
            </div>
          </div>
        </div>
      ),
      skipButton: true
    }
  ];

  // Handle delayed button display
  useEffect(() => {
    let currentSteps;
    if (mode === "checkin") currentSteps = checkinSteps;
    else if (mode === "reflect") currentSteps = reflectSteps;
    else if (mode === "okay") currentSteps = okaySteps;
    else return;
    
    const currentStep = currentSteps[step];
    
    if (currentStep?.buttonDelay) {
      setShowDelayedButton(false);
      const timer = setTimeout(() => setShowDelayedButton(true), currentStep.buttonDelay);
      return () => clearTimeout(timer);
    } else {
      setShowDelayedButton(true);
    }
  }, [step, mode]);

  // Menu screen
  const menuContent = (
    <div className="text-center">
      <h1 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Luma
      </h1>
      <p className="text-[#6B6B6B] text-lg mb-12">A gentle return to the body.</p>
      
      <div className="space-y-4 max-w-sm mx-auto">
        <button
          onClick={() => { setMode("checkin"); setStep(0); }}
          className="w-full p-6 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all bg-white"
          data-testid="luma-start-checkin"
        >
          <h3 className="text-[#3D3D3D] font-medium mb-1">Guided Check-in</h3>
          <p className="text-[#8B8B8B] text-sm">A short reset with breathing and grounding</p>
        </button>
        
        <button
          onClick={() => { setMode("reflect"); setStep(0); }}
          className="w-full p-6 rounded-xl border border-[#E8E4DE] hover:border-[#C9A66B]/50 text-left transition-all bg-white"
          data-testid="luma-start-reflect"
        >
          <h3 className="text-[#3D3D3D] font-medium mb-1">Talk to Luma</h3>
          <p className="text-[#8B8B8B] text-sm">A quiet space to reflect</p>
        </button>
      </div>
      
      <div className="mt-8 pt-8 border-t border-[#E8E4DE]">
        <p className="text-[#6B6B6B] text-sm mb-3">Ready for something deeper?</p>
        <a 
          href="/live"
          className="inline-block bg-[#C9A66B] text-white px-6 py-3 rounded-full hover:bg-[#B8956A] transition-colors text-sm"
          data-testid="luma-menu-join-live"
        >
          Join a Live Session
        </a>
      </div>
    </div>
  );

  const currentSteps = mode === "checkin" ? checkinSteps : mode === "okay" ? okaySteps : reflectSteps;
  const currentStep = currentSteps[step];
  const totalSteps = currentSteps.length - 2;

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col" data-testid="luma-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF8F5]/90 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => { setMode("menu"); setStep(0); setBreathCycle(0); setBreathStep(0); setIsBreathing(false); setAnswers({}); }}
            className="text-[#3D3D3D] font-serif text-xl hover:text-[#C9A66B] transition-colors" 
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Luma
          </button>
          <span className="text-[#8B8B8B] text-sm">
            {mode !== "menu" && step > 0 && step < currentSteps.length - 1 && `${step} / ${totalSteps}`}
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <div className={`max-w-lg w-full transition-opacity duration-400 ${fadeState === "fading" ? "opacity-0" : "opacity-100"}`}>
          {mode === "menu" ? menuContent : currentStep?.content}
          
          {mode !== "menu" && !currentStep?.skipButton && !currentStep?.auto && currentStep?.button && showDelayedButton && (
            <div className={`mt-12 text-center transition-opacity duration-500 ${currentStep?.buttonDelay ? 'animate-fade-in' : ''}`}>
              <button
                onClick={nextStep}
                className="bg-[#C9A66B] text-white px-8 py-4 rounded-full hover:bg-[#B8956A] transition-colors text-sm tracking-wide"
              >
                {currentStep.button}
              </button>
            </div>
          )}
        </div>
      </main>

      {mode !== "menu" && step > 0 && step < currentSteps.length - 1 && (
        <div className="fixed bottom-8 left-0 right-0 flex justify-center gap-2">
          {[...Array(Math.min(8, totalSteps))].map((_, i) => (
            <div 
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${i <= Math.floor((step / totalSteps) * 7) ? "bg-[#C9A66B]" : "bg-[#E8E4DE]"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Luma;
